/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { createContext, useContext } from 'react'

import type { AnyProject, AnyOptionalLinkConfiguration } from '@modules/project-api/types'
import type { AnyRegion } from '@modules/cluster-api/types'
import type { ProjectApiError, ProjectID, ProjectType } from '@modules/ui-types/projects'
import type { QueryHookOptions } from '@modules/query/types'
import type { LinkedCandidatesList } from '@modules/project-user-api/v1/types'

import type { QueryObserverBaseResult, UseMutationResult, UseQueryResult } from 'react-query'

export type ProjectListResponse<T extends AnyProject = AnyProject> = {
  items: T[]
}

export type UseGetProjectQueryHook = (
  type: ProjectType,
  id: ProjectID,
  options?: QueryHookOptions,
) => QueryObserverBaseResult<AnyProject | undefined, ProjectApiError>

/**
 * Query parameters for listing projects.
 * Used by both admin and user console providers.
 */
export type ListProjectsQueryParams = {
  include_deleted?: boolean
  linked?: string
}

/**
 * Hook type for listing projects.
 * Accepts optional query params and options.
 */
export type UseListProjectsQueryHook<T extends AnyProject = AnyProject> = (
  queryParams?: ListProjectsQueryParams,
  options?: QueryHookOptions,
) => UseQueryResult<ProjectListResponse<T>, unknown>

/**
 * Variables type for patch project mutations.
 * The body structure supports the linked projects feature used in CPS.
 * This is a flexible type that accommodates both admin and user API structures.
 */
export type PatchProjectMutationVariables = {
  id: string
  body: {
    linked?: AnyOptionalLinkConfiguration
    // Allow other properties that might be in the actual Patch*ProjectRequest types
    [key: string]: unknown
  }
}

export type UsePatchProjectMutationHook = () => UseMutationResult<
  AnyProject,
  ProjectApiError,
  PatchProjectMutationVariables
>

export type UseListRegionsQueryHook = () => UseQueryResult<AnyRegion[], unknown>

/**
 * Hook type for fetching link candidates for a project.
 * Returns compatible projects that can be linked to the current project.
 */
export type UseGetLinkCandidatesQueryHook = (
  projectType: ProjectType,
  projectId: ProjectID,
  options?: QueryHookOptions,
) => UseQueryResult<LinkedCandidatesList, unknown>

/**
 * Context value type containing all hooks needed by ProjectCps components
 */
export type ProjectCpsContextValue = {
  useGetProjectQuery: UseGetProjectQueryHook

  useListElasticsearchProjectsQuery: UseListProjectsQueryHook

  useListObservabilityProjectsQuery: UseListProjectsQueryHook

  useListSecurityProjectsQuery: UseListProjectsQueryHook

  usePatchElasticsearchProjectMutation: UsePatchProjectMutationHook

  usePatchObservabilityProjectMutation: UsePatchProjectMutationHook

  usePatchSecurityProjectMutation: UsePatchProjectMutationHook

  useListRegionsQuery: UseListRegionsQueryHook

  useGetLinkCandidatesQuery: UseGetLinkCandidatesQueryHook
}

const ProjectCpsContext = createContext<ProjectCpsContextValue | null>(null)

/**
 * Hook to access the ProjectCps context.
 * Must be used within a ProjectCpsProvider.
 *
 * @throws Error if used outside of a ProjectCpsProvider
 */
export const useProjectCpsContext = (): ProjectCpsContextValue => {
  const context = useContext(ProjectCpsContext)

  if (!context) {
    throw new Error(
      'useProjectCpsContext must be used within a ProjectCpsProvider. ' +
        'Wrap your component tree with <ProjectCpsProvider value={...}>.',
    )
  }

  return context
}

type ProjectCpsProviderProps = {
  value: ProjectCpsContextValue
  children: React.ReactNode
}

/**
 * Provider component for the ProjectCps context.
 * Provides the API hooks to all ProjectCps components.
 */
export const ProjectCpsProvider: React.FunctionComponent<ProjectCpsProviderProps> = ({
  value,
  children,
}) => <ProjectCpsContext.Provider value={value}>{children}</ProjectCpsContext.Provider>
